import { useCallback, useReducer, useState } from 'react'

import { backLabel, detailStackReducer, type DetailFrame, type DetailStack } from '../lib/detail-stack'

/** Where an issue drill-down was opened from, so its "back" can return there. Never an issue itself. */
export type GisIssueOrigin = { kind: 'site'; id: string } | null

export type GisSelection =
  | { kind: 'site'; id: string }
  | { kind: 'issue'; id: string; siteId: string | null; from: GisIssueOrigin }
  | { kind: 'officer'; id: string; name: string }
  | null

/** The map's highlight/fly-to input, derived from the active detail frame (so the map needs no stack
 *  awareness). Site/issue frames map to their GisSelection; HOD has no map representation. */
function frameToSelection(frame: DetailFrame): GisSelection {
  if (frame.kind === 'site') {
    return { kind: 'site', id: frame.siteId }
  }
  if (frame.kind === 'issue') {
    return { kind: 'issue', id: frame.id, siteId: frame.siteId ?? null, from: null }
  }
  return null
}

/**
 * Selection has two layers that never interfere:
 * - `selection` — the **scope** selection (`mandal`/`officer`/`null`), read by the panel and (via
 *   `mapSelection`) the map. Site/issue/hod detail no longer live here.
 * - the **detail drill stack** (`DetailFrame[]`) — Issue → Site → HOD, with Back popping one level.
 * The map consumes `mapSelection` (the active frame's GisSelection, else the scope selection), so the
 * map file and the cross-filter memo stay untouched.
 */
export function useGisSelection() {
  const [selection, setSelection] = useState<GisSelection>(null)
  const [stack, dispatch] = useReducer(detailStackReducer, [] as DetailStack)

  const clearDetail = useCallback(() => dispatch({ type: 'clear' }), [])
  const clear = useCallback(() => {
    setSelection(null)
    dispatch({ type: 'clear' })
  }, [])
  const pushDetail = useCallback((frame: DetailFrame) => dispatch({ type: 'push', frame }), [])
  const popDetail = useCallback(() => dispatch({ type: 'pop' }), [])

  const currentFrame: DetailFrame | null = stack.length > 0 ? (stack[stack.length - 1] ?? null) : null
  const mapSelection: GisSelection = currentFrame ? frameToSelection(currentFrame) : selection

  return {
    selection,
    setSelection,
    clear,
    detailStack: stack,
    currentFrame,
    detailDepth: stack.length,
    detailBackLabel: backLabel(stack),
    pushDetail,
    popDetail,
    clearDetail,
    mapSelection,
  }
}
